import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Droplet, ChevronDown, Wind, Wrench, Phone, Clock, CheckCircle, 
  ArrowRight, Shield, Zap, ThermometerSun, Settings
} from 'lucide-react';
import { Helmet } from 'react-helmet';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0); // First item open by default

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const faqs = [
    {
      question: "What areas do you service?",
      answer: "We proudly serve Budd Lake, Mount Olive, and surrounding towns throughout Morris County, New Jersey, including Hackettstown, Roxbury, Chester, Netcong, Stanhope, and more. If you're not sure whether you're in our service area, give us a call at",
      hasPhone: true,
      boldText: "Budd Lake, Mount Olive, and surrounding towns throughout Morris County, New Jersey"
    },
    {
      question: "Do you provide 24/7 emergency plumbing and HVAC services?",
      answer: "Yes. We offer 24/7 emergency response for plumbing, heating, and air conditioning issues. Whether it's a burst pipe at midnight or a broken furnace on a holiday, we're here when you need us most.",
      boldText: ["24/7 emergency response"]
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes. Mount Olive Plumbing is fully licensed and insured in the State of New Jersey, and all work is performed by trained, qualified technicians.",
      boldText: ["fully licensed and insured in the State of New Jersey"]
    },
    {
      question: "What types of services do you offer?",
      answer: "We provide complete residential and commercial services, including:",
      boldText: ["residential and commercial"],
      list: [
        "Plumbing repairs and installations",
        "Drain cleaning",
        "Water heater (tank and tankless) services",
        "Boiler and furnace repairs",
        "AC installations and repairs",
        "Heating system maintenance",
        "Emergency services and more"
      ]
    },
    {
      question: "Do you charge for estimates?",
      answer: "We offer free estimates for most standard jobs. For certain large-scale commercial or complex residential projects, we may recommend an on-site inspection, but you'll be notified in advance if there is any charge.",
      boldText: ["free estimates"]
    },
    {
      question: "What forms of payment do you accept?",
      answer: "We accept cash, major credit cards, and electronic payments. Some large projects may qualify for financing options, which we can discuss during your estimate.",
      boldText: ["cash, major credit cards, and electronic payments", "financing options"]
    },
    {
      question: "How do I schedule an appointment?",
      answer: "You can request service by:",
      list: [
        "Calling us at 973-862-0606",
        "Filling out our online contact form",
        "Messaging us through Google Business Profile or Facebook"
      ],
      listBoldText: ["973-862-0606", "online contact form", "Google Business Profile or Facebook"]
    },
    {
      question: "Do you work with both homeowners and businesses?",
      answer: "Yes. We service both residential homes and commercial buildings, including retail spaces, offices, apartment buildings, and industrial properties.",
      boldText: ["residential", "commercial"]
    },
    {
      question: "Can I see examples of your past work?",
      answer: "Absolutely! Visit our Gallery page to view photos of real plumbing, heating, and AC projects we've completed throughout the area.",
      boldText: ["Gallery page"]
    },
    {
      question: "How can I leave a review?",
      answer: "We welcome feedback and would love to hear about your experience. You can leave a Google review here:",
      hasLink: true
    }
  ];

  const formatTextWithBold = (text, boldTerms) => {
    if (!boldTerms) return text;
    
    // Convert to array if it's a string
    const termsArray = Array.isArray(boldTerms) ? boldTerms : [boldTerms];
    
    if (termsArray.length === 0) return text;
    
    let formattedText = text;
    termsArray.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      formattedText = formattedText.replace(regex, '<strong>$1</strong>');
    });
    
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
  <title>FAQ | Plumbing, Heating & AC Questions | Mount Olive Plumbing</title>
  <meta name="description" content="Answers to common plumbing, heating & cooling questions in Mount Olive, Budd Lake & surrounding NJ areas. 24/7 emergency service available." />
  
  <meta property="og:title" content="Plumbing & HVAC FAQ – Mount Olive NJ" />
  <meta property="og:description" content="Get answers about emergency service, pricing, service areas, and more." />
  <meta property="og:url" content="https://www.mtoliveplumbing.com/faq" />
</Helmet>
      {/* Header Spacer */}
      <div className="h-24"></div>

{/* Hero Section with Background Image */}
<div className="relative py-20 text-white overflow-hidden">
  {/* Background Image - adjusted for better visibility */}
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{
      backgroundImage: "url('/images/bg-about_hero.webp')",
      filter: "brightness(1.2)" // Brighten the image
    }}
  ></div>
  
  {/* Lighter overlay with gradient for better image visibility */}
  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
  
  <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
          className="text-center space-y-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
    {/* Badge */}
    <motion.div
      className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-full font-semibold backdrop-blur-sm bg-white/20 border border-white/60 mb-8"
      whileHover={{ scale: 1.05 }}
      
    >
      <Zap className="h-5 w-5 text-red-500" />
      <span>Residential and Commercial Solutions Available 24/7</span>
    </motion.div>
    
    <h1 className="text-5xl lg:text-6xl font-bold leading-tight drop-shadow-lg mb-6">
      Frequently Asked Questions (FAQ)
    </h1>
    
    <p className="text-white/95 text-lg mb-10 max-w-3xl mx-auto drop-shadow-md">
      Have a question? You're not alone. Below are the most common questions we receive from
      homeowners and business owners across Morris County.
    </p>
    
    <motion.a
      href="tel:+1-973-862-0606"
      className="inline-flex items-center space-x-2 bg-white text-[#A51E29] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      
    >
      <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
      <span>Call Now: (973) 862-0606</span>
    </motion.a>
    
    {/* Stats */}
    <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-12">
      <div className="text-center">
        <div className="text-3xl font-bold text-cyan-300 drop-shadow-lg">20+</div>
        <div className="text-white/90 text-sm mt-2">Years Experience</div>
      </div>
      <div className="text-center">
        <div className="text-3xl font-bold text-cyan-300 drop-shadow-lg">5000+</div>
        <div className="text-white/90 text-sm mt-2">Happy Customers</div>
      </div>
      <div className="text-center">
        <div className="text-3xl font-bold text-cyan-300 drop-shadow-lg">24/7</div>
        <div className="text-white/90 text-sm mt-2">Emergency Service</div>
      </div>
    </div>
    </motion.div>
  </div>
</div>

      {/* FAQ Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg overflow-hidden"
                style={{ boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.08)' }}
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-200 focus:outline-none group"
                >
                  <h3 
                    className={`font-bold pr-4 transition-colors duration-200 ${
                      openIndex === index 
                        ? 'text-[#235FE7]' 
                        : 'text-black group-hover:text-[#235FE7]'
                    }`}
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '24px',
                      fontWeight: '700',
                      lineHeight: '120%'
                    }}
                  >
                     {faq.question}
                  </h3>
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-[#235FE7]">
                    <ChevronDown 
                      className={`h-5 w-5 text-white transition-transform duration-200 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[500px]' : 'max-h-0'
                }`}>
                  <div className="px-6 pb-5">
                    {faq.list ? (
                      <>
                        <p className="text-gray-700 leading-relaxed mb-3" style={{ fontSize: '17px' }}>
                          {formatTextWithBold(faq.answer, faq.boldText)}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700" style={{ fontSize: '17px', paddingLeft: '20px' }}>
                          {faq.list.map((item, i) => {
                            const boldTerms = faq.listBoldText || [];
                            let formattedItem = item;
                            boldTerms.forEach(term => {
                              if (item.includes(term)) {
                                formattedItem = item.replace(term, `<strong>${term}</strong>`);
                              }
                            });
                            return (
                              <li key={i} dangerouslySetInnerHTML={{ __html: formattedItem }} />
                            );
                          })}
                        </ul>
                      </>
                    ) : faq.hasLink ? (
                      <p className="text-gray-700 leading-relaxed" style={{ fontSize: '17px' }}>
                        {faq.answer}{' '}
                        <a 
                          href="https://g.page/r/Cfgws7UsSmMUEAE/review"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#235FE7] hover:underline font-semibold"
                        >
                          https://g.page/r/Cfgws7UsSmMUEAE/review
                        </a>
                      </p>
                    ) : (
                      <p className="text-gray-700 leading-relaxed" style={{ fontSize: '17px' }}>
                        {formatTextWithBold(faq.answer, faq.boldText)}
                        {faq.hasPhone && (
                          <>
                            {' '}
                            <a 
                              href="tel:973-862-0606" 
                              className="text-[#235FE7] hover:underline font-semibold"
                              style={{ fontSize: '17px' }}
                            >
                              973-862-0606
                            </a>
                            <span>.</span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Help Section */}
          {/* <div className="mt-12 bg-blue-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-3">Still Have Questions?</h2>
            <p className="text-blue-100 mb-6">
              Our friendly team is here to help. Contact us for any additional information you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+19738620606"
                className="inline-flex items-center justify-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors duration-300"
              >
                <Phone className="h-5 w-5" />
                <span>Call: (973) 862-0606</span>
              </a>
              <a 
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 bg-blue-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors duration-300"
              >
                <span>Contact Form</span>
              </a>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;